import React, { useState } from "react";
import { ProtectContext } from "../../context/protect_context";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { IMAGE_BASE_URL } from "../../utils/image_url";
import type { Page } from "../../types";

type MobileNavProps = {
  open: boolean;
  onClose: () => void;
};

export const MobileNav: React.FC<MobileNavProps> = ({ open, onClose }) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const { data } = React.useContext(ProtectContext);
  const location = useLocation();

  const toggleItem = (label: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const handleLinkClick = () => {
    onClose();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname === `/${href}`;
  };

  return (
    <div
      className={`fixed inset-0 z-[60] transform transition duration-300 h-screen ${
        open ? "pointer-events-auto" : "pointer-events-none"
      } lg:hidden`}
      aria-hidden={!open}
    >
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <aside
        className={`absolute left-0 top-0 flex h-full w-80 max-w-[85%] flex-col bg-[#1e2229] shadow-2xl transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Цэс"
      >
        <div className="flex h-full flex-col overflow-y-auto px-2 py-2 bg-[#1e2229]">
          <div className="flex w-full justify-end">
            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-full text-white transition hover:text-red-400"
              aria-label="Цэс хаах"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="flex items-center justify-center  py-2">
            <Link
              to="/"
              className="flex items-center gap-3"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              {data?.config?.logo ? (
                <img
                  src={`${IMAGE_BASE_URL}${data.config.logo}`}
                  alt="Бэлэн бай"
                  className="h-10 w-auto"
                />
              ) : null}
            </Link>
          </div>
          <ul className="flex flex-col gap-4 text-sm font-semibold text-white">
            {data?.datas?.map((item: Page) => {
              const isExpanded = expandedItems.has(item.page);
              const hasChildren = item.pages && item.pages.length > 0;

              return (
                <li key={item.page}>
                  {hasChildren ? (
                    <button
                      type="button"
                      onClick={() => toggleItem(item.page)}
                      className="flex w-full items-center justify-between px-4 py-3 transition hover:text-red-400"
                    >
                      {item.page}
                      <svg
                        className={`h-4 w-4 text-white transition-transform duration-200 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m6 9 6 6 6-6"
                        />
                      </svg>
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className={`flex items-center justify-between px-4 py-3 transition ${
                        isActive(item.href)
                          ? "text-red-400"
                          : "hover:text-red-400"
                      }`}
                      onClick={handleLinkClick}
                    >
                      {item.page}
                    </Link>
                  )}
                  {hasChildren && (
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        isExpanded
                          ? "max-h-96 opacity-100"
                          : "max-h-0 opacity-0"
                      }`}
                    >
                      <ul className="mt-2 space-y-2 pl-4 text-sm font-medium">
                        {item.pages.map((subPage: Page) => (
                          <li key={subPage.page}>
                            <Link
                              to={subPage.href}
                              className={`block px-3 py-2 transition ${
                                isActive(subPage.href)
                                  ? "text-red-400"
                                  : "text-white/80 hover:text-red-400"
                              }`}
                              onClick={handleLinkClick}
                            >
                              {subPage.page}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
            <div className="flex flex-col gap-6 w-full px-2">
              <Link
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  onClose();
                }}
                to={"/forum"}
              >
                <button
                  type="button"
                  className="rounded-full px-2 py-1.5 text-sm font-bold text-white"
                >
                  Форум
                </button>
              </Link>
              {/* <button
                type="button"
                className="rounded-full px-2 py-1.5 text-sm font-bold text-white"
                style={{
                  background: "linear-gradient(to right, #f97316, #ef4444)",
                }}
              >
                Нэвтрэх
              </button> */}
            </div>
          </ul>
        </div>
      </aside>
    </div>
  );
};
